import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Users, GitMerge, ArrowRightLeft, FileWarning, Loader2, CheckCircle2,
  ArrowRight, AlertTriangle, RefreshCw, Scissors, Tag, X, UserX, Link2, RotateCcw, Hash,
} from "lucide-react";
import { toast } from "sonner";

const Stat = ({ label, value, tone = "default", testid }) => (
  <div data-testid={testid} className={`rounded-lg border p-3 ${
    tone === "warn" ? "border-amber-200 bg-amber-50/60"
      : tone === "bad" ? "border-rose-200 bg-rose-50/60"
      : tone === "good" ? "border-emerald-200 bg-emerald-50/60"
      : "border-slate-200 bg-white"}`}>
    <div className="text-2xl font-display font-semibold text-slate-900">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{label}</div>
  </div>
);

const SectionCard = ({ title, icon: Icon, desc, children, actions }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4">
    <div className="flex items-start gap-2 mb-3">
      <Icon className="w-4 h-4 text-slate-500 mt-0.5" />
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {desc && <p className="text-xs text-slate-500 mt-0.5">{desc}</p>}
      </div>
      {actions}
    </div>
    {children}
  </div>
);

export const DataQualityTab = ({ canEdit = false, onComplianceChanged }) => {
  const [dupes, setDupes] = useState(null);
  const [recon, setRecon] = useState(null);
  const [diag, setDiag] = useState(null);
  const [aliases, setAliases] = useState(null);
  const [aliasBusy, setAliasBusy] = useState(null);
  const [aliasFilter, setAliasFilter] = useState("");
  const [runDetail, setRunDetail] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(null);
  const [swap, setSwap] = useState({});
  const [reconView, setReconView] = useState("only_sharepoint");
  const [unmatched, setUnmatched] = useState(null);
  const [missing, setMissing] = useState(null);
  const [emps, setEmps] = useState([]);
  const [linkSel, setLinkSel] = useState({});
  const [umBusy, setUmBusy] = useState(null);
  const [rematching, setRematching] = useState(false);
  const [mergeKeep, setMergeKeep] = useState("");
  const [mergeRetire, setMergeRetire] = useState("");
  const [manualMerging, setManualMerging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, r, g] = await Promise.all([
        api.get("/hr-hub/data-quality/duplicates"),
        api.get("/hr-hub/data-quality/reconcile"),
        api.get("/hr-hub/data-quality/import-diagnostics"),
      ]);
      setDupes(d.data); setRecon(r.data); setDiag(g.data);
      setRunDetail(g.data.latest || null);
      setSelectedRunId(g.data.latest?.id || null);
      api.get("/hr-hub/data-quality/aliases").then((a) => setAliases(a.data)).catch(() => {});
      api.get("/hr-hub/compliance/unmatched").then((u) => setUnmatched(u.data)).catch(() => {});
      api.get("/hr-hub/data-quality/compliance-gaps").then((m) => setMissing(m.data)).catch(() => {});
      api.get("/hr-hub/employees?status=all&limit=1000")
        .then((e) => setEmps(e.data.items || [])).catch(() => {});
    } catch (e) {
      toast.error("Failed to load data quality report");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const refreshUnmatched = async () => {
    try {
      const u = await api.get("/hr-hub/compliance/unmatched");
      setUnmatched(u.data);
      api.get("/hr-hub/data-quality/compliance-gaps").then((m) => setMissing(m.data)).catch(() => {});
    } catch { /* noop */ }
    onComplianceChanged?.();
  };

  const rerunMatching = async () => {
    setRematching(true);
    try {
      const { data } = await api.post("/hr-hub/compliance/rebuild");
      const left = data?.unmatched_names ?? 0;
      toast.success(
        left === 0
          ? "Matching re-run — every compliance row is now matched."
          : `Matching re-run — ${left} name${left === 1 ? "" : "s"} still unmatched.`
      );
      await refreshUnmatched();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to re-run matching");
    } finally { setRematching(false); }
  };

  const dismissName = async (name) => {
    if (!window.confirm(`Dismiss "${name}" from the unmatched compliance warning?\n\nUse this when the person has left the company. Their orphaned register rows stay on record but stop triggering the warning. You can restore this later.`)) return;
    setUmBusy(`dismiss:${name}`);
    try {
      await api.post("/hr-hub/compliance/unmatched/ignore", { name });
      toast.success(`"${name}" dismissed`);
      await refreshUnmatched();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to dismiss");
    } finally { setUmBusy(null); }
  };

  const restoreName = async (name) => {
    setUmBusy(`restore:${name}`);
    try {
      await api.post("/hr-hub/compliance/unmatched/unignore", { name });
      toast.success(`"${name}" restored`);
      await refreshUnmatched();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to restore");
    } finally { setUmBusy(null); }
  };

  const linkName = async (name) => {
    const employee_id = linkSel[name];
    if (!employee_id) { toast.error("Pick an employee to link to"); return; }
    setUmBusy(`link:${name}`);
    try {
      const { data } = await api.post("/hr-hub/compliance/unmatched/assign", { name, employee_id });
      toast.success(`"${name}" linked to ${data.employee} (${data.rebound} row${data.rebound === 1 ? "" : "s"})`);
      setLinkSel((s) => { const n = { ...s }; delete n[name]; return n; });
      await refreshUnmatched();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to link");
    } finally { setUmBusy(null); }
  };

  const selectRun = async (id) => {
    setSelectedRunId(id);
    if (diag?.latest && id === diag.latest.id) { setRunDetail(diag.latest); return; }
    try {
      const { data } = await api.get(`/hr-hub/data-quality/import-diagnostics/${id}`);
      setRunDetail(data);
    } catch (e) {
      toast.error("Failed to load that import run");
    }
  };

  const doMerge = async (pair, idx) => {
    const flip = swap[idx];
    const primary = flip ? pair.duplicate : pair.primary;
    const duplicate = flip ? pair.primary : pair.duplicate;
    if (!window.confirm(`Merge "${duplicate.name}" INTO "${primary.name}"?\n\n"${duplicate.name}" will be retired and its name saved as an alias on "${primary.name}". This cannot be auto-undone.`)) return;
    setMerging(idx);
    try {
      const { data } = await api.post("/hr-hub/data-quality/merge", {
        primary_ref: primary.emp_ref, duplicate_ref: duplicate.emp_ref,
      });
      toast.success(`Merged ${data.duplicate} → ${data.primary}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally { setMerging(null); }
  };

  const empLabel = (id) => {
    const e = emps.find((x) => x.id === id);
    if (!e) return "";
    return `${e.first_name} ${e.last_name}${e.active === false ? " (inactive)" : ""}${e.email ? ` · ${e.email}` : ""}`;
  };

  const manualMerge = async () => {
    if (!mergeKeep || !mergeRetire) { toast.error("Pick both records"); return; }
    if (mergeKeep === mergeRetire) { toast.error("Pick two different records"); return; }
    if (!window.confirm(`Merge "${empLabel(mergeRetire)}" INTO "${empLabel(mergeKeep)}"?\n\nThe KEEP record (and its email) survives. The other is retired and its name saved as an alias. Reversible via Split.`)) return;
    setManualMerging(true);
    try {
      const { data } = await api.post("/hr-hub/data-quality/merge", {
        primary_ref: mergeKeep, duplicate_ref: mergeRetire,
      });
      toast.success(`Merged ${data.duplicate} → ${data.primary}`);
      setMergeKeep(""); setMergeRetire("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally { setManualMerging(false); }
  };

  const removeAliasString = async (emp, alias) => {
    if (!window.confirm(`Remove the alias "${alias}" from ${emp.name}?\n\nRecords that only matched on this spelling will no longer link to ${emp.name} (they'll show as unmatched until re-tagged).`)) return;
    setAliasBusy(`${emp.emp_ref}:${alias}`);
    try {
      const next = emp.aliases.filter((a) => a.alias !== alias).map((a) => a.alias);
      await api.put(`/hr-hub/employees/${emp.emp_ref}/aliases`, { aliases: next });
      toast.success(`Alias "${alias}" removed`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove alias");
    } finally { setAliasBusy(null); }
  };

  const splitOut = async (emp, item) => {
    if (!window.confirm(`Split "${item.alias}" back out of ${emp.name}?\n\n"${item.alias}" becomes its own active employee again and its pay data moves back. Contact fields that were back-filled onto ${emp.name} during the merge are not auto-reverted.`)) return;
    setAliasBusy(`${emp.emp_ref}:${item.alias}`);
    try {
      const { data } = await api.post("/hr-hub/data-quality/unmerge", {
        primary_ref: emp.emp_ref, duplicate_ref: item.merged_ref,
      });
      toast.success(`${data.duplicate} split back out of ${data.primary}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Split failed");
    } finally { setAliasBusy(null); }
  };

  const aliasRows = useMemo(() => {
    const list = aliases?.employees || [];
    const q = aliasFilter.trim().toLowerCase();
    if (!q) return list;
    return list.filter((e) => e.name.toLowerCase().includes(q)
      || (e.aliases || []).some((a) => a.alias.toLowerCase().includes(q)));
  }, [aliases, aliasFilter]);

  const reconRows = useMemo(() => {
    if (!recon) return [];
    return reconView === "only_aroflo" ? recon.only_in_aroflo
      : reconView === "in_both" ? recon.in_both_sample
      : recon.only_in_sharepoint;
  }, [recon, reconView]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm p-8" data-testid="dq-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Building data quality report…
      </div>
    );
  }

  const latest = runDetail;
  const fmtRun = (r) => `${(r.uploaded_at || "").replace("T", " ").slice(0, 16)} · ${r.rows_inserted}+ / ${r.rows_updated}✎ / ${r.rows_errored}✗`;

  return (
    <div className="space-y-4" data-testid="dq-tab">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={load} data-testid="dq-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Unmatched compliance names */}
      <SectionCard
        title="Unmatched compliance names"
        icon={UserX}
        desc="Register rows whose name matches no employee. Re-run matching after an employee is added, link a misspelling to the right person, or dismiss someone who has left the company so the warning clears."
        actions={canEdit && (
          <Button size="sm" variant="outline" onClick={rerunMatching}
            disabled={rematching} data-testid="dq-rerun-matching-btn">
            {rematching ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Re-run matching
          </Button>
        )}>
        {!unmatched ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-3" data-testid="dq-unmatched-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading unmatched names…
          </div>
        ) : (unmatched.items || []).length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700 py-3" data-testid="dq-unmatched-empty">
            <CheckCircle2 className="w-4 h-4" /> Every compliance row is matched to an employee.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="dq-unmatched-list">
            {unmatched.items.map((u) => (
              <li key={u.name} data-testid={`dq-unmatched-${u.name}`} className="py-3 flex items-center gap-3 flex-wrap">
                <div className="min-w-[180px] flex-1">
                  <div className="text-sm font-medium text-slate-900">{u.name}</div>
                  <div className="text-[11px] text-slate-400">{u.rows} register row{u.rows === 1 ? "" : "s"} · no employee match</div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <select
                      data-testid={`dq-unmatched-select-${u.name}`}
                      value={linkSel[u.name] || ""}
                      onChange={(e) => setLinkSel((s) => ({ ...s, [u.name]: e.target.value }))}
                      className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white max-w-[220px]">
                      <option value="">Link to employee…</option>
                      {emps.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.first_name} {e.last_name}{e.active === false ? " (inactive)" : ""}{e.email ? ` · ${e.email}` : ""}
                        </option>
                      ))}
                    </select>
                    <Button size="sm" variant="outline" data-testid={`dq-unmatched-link-${u.name}`}
                      disabled={!linkSel[u.name] || umBusy === `link:${u.name}`}
                      onClick={() => linkName(u.name)}>
                      {umBusy === `link:${u.name}` ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Link2 className="w-3.5 h-3.5 mr-1" />}
                      Link
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`dq-unmatched-dismiss-${u.name}`}
                      disabled={umBusy === `dismiss:${u.name}`}
                      onClick={() => dismissName(u.name)}
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                      {umBusy === `dismiss:${u.name}` ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UserX className="w-3.5 h-3.5 mr-1" />}
                      Left company
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {(unmatched?.dismissed || []).length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100" data-testid="dq-unmatched-dismissed">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Dismissed (left company)</div>
            <div className="flex flex-wrap gap-1.5">
              {unmatched.dismissed.map((d) => (
                <span key={d.name} data-testid={`dq-dismissed-${d.name}`}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-500">
                  {d.name}
                  {canEdit && (
                    <button data-testid={`dq-restore-${d.name}`} disabled={umBusy === `restore:${d.name}`}
                      onClick={() => restoreName(d.name)} className="hover:text-slate-900 disabled:opacity-50" title="Restore to warning">
                      {umBusy === `restore:${d.name}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      {/* Compliance data gaps */}
      <SectionCard
        title={`Compliance data gaps${missing?.count ? ` (${missing.count})` : ""}`}
        icon={Hash}
        desc="Tracked licences, certifications and inductions with missing data — no number, no expiry date, or no document on file. Your pre-audit checklist: fix the source (SharePoint / Import Hub) then re-run matching."
        actions={canEdit && (
          <Button size="sm" variant="outline" onClick={rerunMatching}
            disabled={rematching} data-testid="dq-missing-rerun-btn">
            {rematching ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Re-run matching
          </Button>
        )}>
        {!missing ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-3" data-testid="dq-missing-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (missing.items || []).length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700 py-3" data-testid="dq-missing-empty">
            <CheckCircle2 className="w-4 h-4" /> No gaps — every tracked record has a number, expiry and document.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="dq-missing-summary">
              {[["number", "missing number"], ["expiry", "missing expiry"], ["document", "no document"]].map(([k, lbl]) => (
                (missing.summary?.[k] || 0) > 0 && (
                  <span key={k} className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] text-amber-700">
                    <span className="font-semibold">{missing.summary[k]}</span> {lbl}
                  </span>
                )
              ))}
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="dq-missing-list">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="text-left font-medium px-3 py-2">Employee</th>
                    <th className="text-left font-medium px-3 py-2">Record</th>
                    <th className="text-left font-medium px-3 py-2">Expiry</th>
                    <th className="text-left font-medium px-3 py-2">Gaps</th>
                  </tr>
                </thead>
                <tbody>
                  {missing.items.map((r) => (
                    <tr key={r.id} data-testid={`dq-missing-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                      <td className="px-3 py-2 font-medium text-slate-800">
                        {r.employee_name}
                        {r.unmatched && <Badge className="ml-1.5 bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">unmatched</Badge>}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1.5">{r.kind}</span>{r.item}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{r.expiry_date ? r.expiry_date.slice(0, 10) : <span className="text-amber-600">—</span>}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {r.gaps.map((g) => (
                            <span key={g} className="inline-flex rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">
                              {g === "number" ? "no number" : g === "expiry" ? "no expiry" : "no document"}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </SectionCard>

      {/* Duplicate finder */}
      <SectionCard
        title="Possible duplicate employees"
        icon={GitMerge}
        desc="Look-alike records from name variants or matching email / AroFlo user. Merging banks the losing name as an alias so it won't re-duplicate.">
        {(dupes?.pairs || []).length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700 py-4" data-testid="dq-dupes-empty">
            <CheckCircle2 className="w-4 h-4" /> No likely duplicates found.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="dq-dupes-list">
            {dupes.pairs.map((p, idx) => {
              const flip = swap[idx];
              const primary = flip ? p.duplicate : p.primary;
              const duplicate = flip ? p.primary : p.duplicate;
              return (
                <li key={idx} data-testid={`dq-dupe-${idx}`} className="py-3 flex items-center gap-3 flex-wrap">
                  <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px] shrink-0">{p.reason}</Badge>
                  <div className="flex items-center gap-2 text-sm flex-1 min-w-[280px]">
                    <div className="text-right flex-1">
                      <div className="font-medium text-slate-900">{duplicate.name}</div>
                      <div className="text-[11px] text-slate-400">{duplicate.email} · {duplicate.rem_records} rem · {duplicate.has_aroflo ? "AroFlo" : "no AroFlo"}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">{primary.name} <span className="text-[10px] text-emerald-700 uppercase tracking-wider">keep</span></div>
                      <div className="text-[11px] text-slate-400">{primary.email} · {primary.rem_records} rem · {primary.has_aroflo ? "AroFlo" : "no AroFlo"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button data-testid={`dq-swap-${idx}`} onClick={() => setSwap((s) => ({ ...s, [idx]: !s[idx] }))}
                      className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" title="Swap which record is kept">
                      <ArrowRightLeft className="w-3.5 h-3.5" /> Swap
                    </button>
                    {canEdit && (
                      <Button size="sm" data-testid={`dq-merge-${idx}`} disabled={merging === idx}
                        onClick={() => doMerge(p, idx)}>
                        {merging === idx ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <GitMerge className="w-3.5 h-3.5 mr-1" />}
                        Merge
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>

      {/* Manual merge — pick any two records */}
      {canEdit && (
        <SectionCard
          title="Merge two records manually"
          icon={GitMerge}
          desc="Can't see a pair above? Pick the two records yourself. The record you KEEP (and its email) survives; the other is retired as an alias. Reversible via Split.">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 min-w-0">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 block mb-1">Keep (survives, email retained)</label>
              <select data-testid="dq-manual-merge-keep" value={mergeKeep}
                onChange={(e) => setMergeKeep(e.target.value)}
                className="w-full text-xs border border-slate-200 rounded-md px-2 py-2 bg-white">
                <option value="">Select record to keep…</option>
                {emps.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.first_name} {e.last_name}{e.active === false ? " (inactive)" : ""}{e.email ? ` · ${e.email}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <ArrowRight className="hidden sm:block w-4 h-4 text-slate-400 mb-2.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 block mb-1">Retire (folded in)</label>
              <select data-testid="dq-manual-merge-retire" value={mergeRetire}
                onChange={(e) => setMergeRetire(e.target.value)}
                className="w-full text-xs border border-slate-200 rounded-md px-2 py-2 bg-white">
                <option value="">Select record to retire…</option>
                {emps.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.first_name} {e.last_name}{e.active === false ? " (inactive)" : ""}{e.email ? ` · ${e.email}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <Button size="sm" data-testid="dq-manual-merge-btn" className="shrink-0"
              disabled={!mergeKeep || !mergeRetire || mergeKeep === mergeRetire || manualMerging}
              onClick={manualMerge}>
              {manualMerging ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <GitMerge className="w-3.5 h-3.5 mr-1" />}
              Merge
            </Button>
          </div>
        </SectionCard>
      )}

      {/* Name aliases — review & re-split */}
      <SectionCard
        title="Name aliases"
        icon={Tag}
        desc="Every alias folded onto a person. Remove a spelling you no longer want, or split a merged record back out into its own employee."
        actions={(aliases?.employees || []).length > 0 && (
          <input
            data-testid="dq-alias-filter"
            value={aliasFilter}
            onChange={(e) => setAliasFilter(e.target.value)}
            placeholder="Filter by name…"
            className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white max-w-[200px]" />
        )}>
        {!aliases ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-3" data-testid="dq-alias-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading aliases…
          </div>
        ) : aliasRows.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-3" data-testid="dq-alias-empty">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            {aliasFilter ? "No aliases match that filter." : "No employees carry aliases yet."}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto" data-testid="dq-alias-list">
            {aliasRows.map((emp) => (
              <li key={emp.emp_ref} data-testid={`dq-alias-emp-${emp.emp_ref}`} className="py-2.5 flex items-start gap-3 flex-wrap">
                <div className="min-w-[180px] flex-1">
                  <div className="text-sm font-medium text-slate-900">{emp.name}</div>
                  <div className="text-[11px] text-slate-400">{emp.email || "—"}</div>
                </div>
                <div className="flex flex-wrap gap-1.5 flex-[2]">
                  {emp.aliases.map((item) => {
                    const busy = aliasBusy === `${emp.emp_ref}:${item.alias}`;
                    const merged = !!item.merged_ref;
                    return (
                      <span key={item.alias} data-testid={`dq-alias-chip-${emp.emp_ref}-${item.alias}`}
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${merged ? "border-indigo-200 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-slate-50 text-slate-600"}`}
                        title={merged ? "Came from a merge — can be split back into its own record" : "Manually added alias"}>
                        {item.alias}
                        {canEdit && (merged ? (
                          <button data-testid={`dq-alias-split-${emp.emp_ref}-${item.alias}`}
                            disabled={busy} onClick={() => splitOut(emp, item)}
                            className="hover:text-indigo-900 disabled:opacity-50" title="Split back out into its own record">
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Scissors className="w-3 h-3" />}
                          </button>
                        ) : (
                          <button data-testid={`dq-alias-remove-${emp.emp_ref}-${item.alias}`}
                            disabled={busy} onClick={() => removeAliasString(emp, item.alias)}
                            className="hover:text-rose-600 disabled:opacity-50" title="Remove this alias">
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                          </button>
                        ))}
                      </span>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {/* Import diagnostics */}
      <SectionCard
        title="SharePoint import diagnostics"
        icon={FileWarning}
        desc="Employee imports from Power Automate — what landed, what updated, and exactly why any row was rejected. Pick a past run to inspect it."
        actions={(diag?.history || []).length > 0 && (
          <select
            data-testid="dq-diag-run-picker"
            value={selectedRunId || ""}
            onChange={(e) => selectRun(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white max-w-[240px]">
            {diag.history.map((r, i) => (
              <option key={r.id} value={r.id}>
                {i === 0 ? "Latest · " : ""}{fmtRun(r)}
              </option>
            ))}
          </select>
        )}>
        {!latest ? (
          <div className="text-sm text-slate-500 py-4" data-testid="dq-diag-empty">{diag?.message || "No employee imports recorded yet."}</div>
        ) : (
          <>
            <div className="text-[11px] text-slate-400 mb-2">
              {(latest.uploaded_at || "").replace("T", " ").slice(0, 16)} · {latest.source || "—"} · {latest.uploaded_by || "—"}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
              <Stat label="Total rows" value={latest.rows_total} testid="dq-diag-total" />
              <Stat label="Inserted" value={latest.rows_inserted} tone="good" testid="dq-diag-inserted" />
              <Stat label="Updated" value={latest.rows_updated} testid="dq-diag-updated" />
              <Stat label="Skipped" value={latest.rows_skipped} testid="dq-diag-skipped" />
              <Stat label="Rejected" value={latest.rows_errored} tone={latest.rows_errored > 0 ? "bad" : "default"} testid="dq-diag-rejected" />
            </div>
            {(latest.errors || []).length > 0 ? (
              <div className="border border-rose-200 rounded-lg overflow-hidden" data-testid="dq-diag-errors">
                <div className="bg-rose-50 px-3 py-1.5 text-[11px] font-medium text-rose-700 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Rejected rows ({latest.errors.length}{latest.errors_truncated ? "+" : ""})
                </div>
                <ul className="divide-y divide-rose-100 max-h-64 overflow-y-auto">
                  {latest.errors.map((er, i) => (
                    <li key={i} className="px-3 py-2 text-xs flex gap-3">
                      <span className="text-slate-400 shrink-0">Row {er.row_index}</span>
                      <span className="text-rose-700">{(er.errors || []).join("; ")}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-emerald-700 py-1" data-testid="dq-diag-clean">
                <CheckCircle2 className="w-4 h-4" /> No rejected rows in the last import.
              </div>
            )}
          </>
        )}
      </SectionCard>

      {/* Reconcile */}
      <SectionCard
        title="AroFlo ↔ SharePoint reconcile"
        icon={ArrowRightLeft}
        desc="Active people who appear in only one system — catch new starters missing from AroFlo, or AroFlo users not yet in the HR list.">
        <div className="grid grid-cols-3 gap-2 mb-3">
          <button onClick={() => setReconView("only_sharepoint")} data-testid="dq-recon-view-sharepoint" className="text-left">
            <Stat label="Only in SharePoint" value={recon?.counts.only_sharepoint ?? 0} tone={reconView === "only_sharepoint" ? "warn" : "default"} testid="dq-recon-sp" />
          </button>
          <button onClick={() => setReconView("only_aroflo")} data-testid="dq-recon-view-aroflo" className="text-left">
            <Stat label="Only in AroFlo" value={recon?.counts.only_aroflo ?? 0} tone={reconView === "only_aroflo" ? "warn" : "default"} testid="dq-recon-af" />
          </button>
          <button onClick={() => setReconView("in_both")} data-testid="dq-recon-view-both" className="text-left">
            <Stat label="In both" value={recon?.counts.in_both ?? 0} tone={reconView === "in_both" ? "good" : "default"} testid="dq-recon-both" />
          </button>
        </div>
        {reconRows.length === 0 ? (
          <div className="text-sm text-slate-500 py-3" data-testid="dq-recon-empty">Nobody in this bucket.</div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="dq-recon-list">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left font-medium px-3 py-2">Employee</th>
                  <th className="text-left font-medium px-3 py-2">Email</th>
                  <th className="text-left font-medium px-3 py-2">Role</th>
                  <th className="text-left font-medium px-3 py-2">BU</th>
                </tr>
              </thead>
              <tbody>
                {reconRows.slice(0, 200).map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-medium text-slate-800">{r.name}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.email}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.role_title}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.business_unit_code}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {reconRows.length > 200 && (
              <div className="text-[10px] text-slate-400 px-3 py-1.5 border-t border-slate-100">Showing 200 of {reconRows.length}</div>
            )}
          </div>
        )}
      </SectionCard>
    </div>
  );
};
